import { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';

import { LoginPage } from './pages/Login.pages';
import { DashboardPage } from './pages/Dashboard.pages';
import { JobListingPage } from './pages/JobListing.pages';
import { NotFoundPage } from './pages/NotFound.pages';
import { Navbar } from './components/custom/Navbar.components';
import { logError } from './utils/logger.utils';
import { ApiTypes } from './types/server/api.types';
import posthog from 'posthog-js';
import { LobbyPage } from './pages/Lobby.pages';
import { InterviewRoomPage } from './pages/InterviewRoom.pages';

export const App = () => {
	const [isAppLoading, setIsAppLoading] = useState(true);
	const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
	const [userDetails, setUserDetails] = useState<
		ApiTypes.TGetCandidateDetailsResponse['data'] | null
	>(null);

	const [audioVideoStream, setAudioVideoStream] = useState<MediaStream | null>(
		null
	);
	const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
	const [devices, setDevices] = useState<{
		speakerId: string | null;
		cameraId: string | null;
		microphoneId: string | null;
	} | null>(null);

	const navigate = useNavigate();
	const location = useLocation();

	const protectedRoutes = ['/lobby'];

	const isProtectedRoute = (pathname: string) => {
		return protectedRoutes.some((route) => pathname.startsWith(route));
	};

	const handleGetLoggedInUser = async () => {
		try {
			// Check for token in query parameters first
			const urlParams = new URLSearchParams(location.search);
			const urlToken = urlParams.get('token');

			if (urlToken) {
				posthog.capture('App_UserLoginWithToken', {
					token: urlToken,
				});

				// Store the token from URL in localStorage
				window.localStorage.setItem('token', urlToken);
				// Remove token from URL to clean up the address bar
				const newUrl = window.location.pathname;
				window.history.replaceState({}, '', newUrl);
			}

			const token = window.localStorage.getItem('token');

			if (!token) {
				if (isProtectedRoute(location.pathname)) {
					navigate('/');
				}
				return;
			}

			const response = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/candidates/details`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
				}
			);

			const data =
				(await response.json()) as ApiTypes.TGetCandidateDetailsResponse;

			if (data.httpResponseCode !== 200) {
				window.localStorage.removeItem('token');
				if (isProtectedRoute(location.pathname)) {
					navigate('/');
				}
				setIsUserLoggedIn(false);
				setUserDetails(null);
				return;
			}

			setIsUserLoggedIn(true);
			setUserDetails(data.data);
		} catch (err) {
			logError(err);
			toast('Error verifying session', {
				description:
					'We are facing some challenges while verifying your session. Please check your network and try again.',
				id: 'get-candidate-details-error',
			});
			window.localStorage.removeItem('token');
			setIsUserLoggedIn(false);
			setUserDetails(null);
			navigate('/');
		} finally {
			setIsAppLoading(false);
		}
	};

	useEffect(() => {
		handleGetLoggedInUser();
	}, []);

	useEffect(() => {
		// when isUserLoggedIn is false, we need to check if the user is in protected route. if so redirect them, otherwise do nothing.

		if (!isAppLoading && !isUserLoggedIn) {
			if (isProtectedRoute(location.pathname)) {
				navigate('/');
			}
		}
	}, [isUserLoggedIn]);

	const handleStartAudioVideoStream = async (
		cameraId?: string,
		microphoneId?: string
	): Promise<MediaStream | null> => {
		let mediaStream: MediaStream | null = null;
		try {
			mediaStream = await navigator.mediaDevices.getUserMedia({
				video: cameraId ? { deviceId: { exact: cameraId } } : true,
				audio: microphoneId ? { deviceId: { exact: microphoneId } } : true,
			});

			setAudioVideoStream(mediaStream);

			// Detect if camera/microphone is stopped
			mediaStream.getTracks().forEach((track) => {
				track.onended = () => {
					setAudioVideoStream(null);
				};
			});

			return mediaStream;
		} catch (err) {
			logError(err);
			mediaStream?.getTracks().forEach((track) => track.stop());

			setAudioVideoStream(null);

			return null;
		}
	};

	const handleStopAudioVideoStream = (): boolean => {
		try {
			audioVideoStream?.getTracks().forEach((track) => track?.stop());
			setAudioVideoStream(null);

			return true;
		} catch (error) {
			logError(error);
			return false;
		}
	};

	if (isAppLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-blue-50">
				<Loader2 className="h-10 w-10 animate-spin text-blue-600" />
			</div>
		);
	}

	return (
		<>
			<div className="w-full min-h-screen bg-blue-50">
				<Routes>
					<Route
						path="/"
						element={
							isUserLoggedIn ? (
								<>
									<Navbar
										isUserLoggedIn={isUserLoggedIn}
										setIsUserLoggedIn={setIsUserLoggedIn}
										userDetails={userDetails}
										setUserDetails={setUserDetails}
									/>
									<DashboardPage
										setIsUserLoggedIn={setIsUserLoggedIn}
										setUserDetails={setUserDetails}
									/>
								</>
							) : (
								<>
									<Navbar
										isUserLoggedIn={isUserLoggedIn}
										setIsUserLoggedIn={setIsUserLoggedIn}
										userDetails={userDetails}
										setUserDetails={setUserDetails}
									/>
									<LoginPage
										setIsUserLoggedIn={setIsUserLoggedIn}
										setUserDetails={setUserDetails}
									/>
								</>
							)
						}
					/>
					<Route
						path="/listing/:slug"
						element={
							<>
								<Navbar
									isUserLoggedIn={isUserLoggedIn}
									setIsUserLoggedIn={setIsUserLoggedIn}
									userDetails={userDetails}
									setUserDetails={setUserDetails}
								/>
								<JobListingPage
									isUserLoggedIn={isUserLoggedIn}
									setIsUserLoggedIn={setIsUserLoggedIn}
									setUserDetails={setUserDetails}
								/>
							</>
						}
					/>
					{/* @NOTE - Don't put this route in ProtectedRoute because for some reason, it will just keep remounting (not even re-rendering). */}
					<Route
						path="/lobby"
						element={
							<>
								<Navbar
									isUserLoggedIn={isUserLoggedIn}
									setIsUserLoggedIn={setIsUserLoggedIn}
									userDetails={userDetails}
									setUserDetails={setUserDetails}
								/>
								<LobbyPage
									startAudioVideoStream={handleStartAudioVideoStream}
									stopAudioVideoStream={handleStopAudioVideoStream}
									audioVideoStream={audioVideoStream}
									setDevices={setDevices}
									setScreenStream={setScreenStream}
								/>
							</>
						}
					/>
					<Route
						path="/interview"
						element={
							<InterviewRoomPage
								devices={devices}
								handleStopAudioVideoStream={handleStopAudioVideoStream}
								audioVideoStream={audioVideoStream}
								screenStream={screenStream}
								setIsUserLoggedIn={setIsUserLoggedIn}
								userDetails={userDetails}
								setScreenStream={setScreenStream}
							/>
						}
					/>
					<Route path="*" element={<NotFoundPage />} />
				</Routes>

				<Toaster position="top-center" />
			</div>
		</>
	);
};
